import posthog from 'posthog-js';

import {
  EventProperties,
  ExternalEventTrackingClient,
  TrackTransportOptions,
} from './types';

export class ZettaClient implements ExternalEventTrackingClient {
  private _initialized = false;

  constructor() {}

  /**
   * PostHog SDK를 초기화합니다. 멱등적: 중복 호출해도 한 번만 실행됩니다.
   * quiz-app에서는 zetta.client.config.ts에서 module-load 시점에 호출됩니다.
   * libs/common/core-logger의 공용 싱글톤은 play-app의 zetta.client.config.ts에서 호출합니다.
   */
  initialize(): void {
    if (this._initialized) return;
    try {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_API_KEY ?? '', {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '',
        // 아래 옵션은 Zetta 운영 측 요청에 의해 설정한 값입니다. 지원하지 않는 기능이라고 합니다.
        capture_pageview: false,
        capture_pageleave: false,
        autocapture: false,
        disable_session_recording: true,
        debug: false,
      });
      this._initialized = true;
    } catch (error) {
      console.error('Failed to initialize Posthog:', error);
    }
  }

  public identify(userId: string, eventProperties?: EventProperties) {
    if (!this._initialized) return;
    posthog.identify(userId, eventProperties);
  }

  /**
   * Note: 이 함수를 호출할 경우, posthog는 현재 유저를 새로운 유저로 식별하므로 주의해서 사용해야 합니다.
   * posthog.identify type definition을 읽어보면, posthog가 어떻게 유저를 식별하는지 자세히 이해할 수 있습니다.
   */
  public resetIdentity() {
    if (!this._initialized) return;
    posthog.reset();
  }

  public setUserProperties(eventProperties: EventProperties) {
    if (!this._initialized) return;
    posthog.setPersonProperties(eventProperties);
  }

  public track(
    eventName: string,
    eventProperties?: EventProperties,
    options?: TrackTransportOptions,
  ) {
    if (!this._initialized) return;
    if (options?.transport === 'sendBeacon') {
      // 큐에 들어가면 실제 전송 시점엔 transport 가 이미 복원돼 있다 — 즉시 전송을 강제한다
      captureWithBeacon(eventName, eventProperties, {
        ...options,
        send_instantly: true,
      });
      return;
    }
    posthog.capture(eventName, eventProperties, options);
  }
}

type TransportConfig = { api_transport?: string };

/**
 * posthog-js 1.178 의 capture 는 옵션의 `transport` 를 요청에 싣지 않아 XHR 로 나간다.
 * 이탈 직전 XHR 은 페이지와 함께 끊길 수 있다. capture → _send_request 는 동기이고
 * transport 가 비면 `config.api_transport` 를 쓰므로, 이 한 번만 sendBeacon 으로 바꿨다 되돌린다.
 */
function captureWithBeacon(
  eventName: string,
  eventProperties: EventProperties | undefined,
  options: TrackTransportOptions,
) {
  // 타입은 'XHR' | 'fetch' 만 열어 두지만 런타임은 sendBeacon 을 지원한다 — 단언은 여기 한 곳
  const config = posthog.config as TransportConfig;
  const previous = config.api_transport;
  config.api_transport = 'sendBeacon';
  try {
    posthog.capture(eventName, eventProperties, options);
  } finally {
    config.api_transport = previous;
  }
}
